/**
 * Columns for the bulk promotion history list.
 *
 * The list query reads a sparse index, so no column can read an attribute the index does not project,
 * and adding one later means recreating the index across every stage. config_snapshot and
 * cap_at_submission are on the batch record but not projected. accounting_period is projected but is
 * the page-level selector rather than a column.
 */
export const BULK_PROMOTION_HISTORY_COLUMN_DEFINITIONS = {
    STARTED: {
        id: 'createdAtDisplay',
        header: 'Started',
        sortingField: 'createdAt',
    },
    BATCH_ID: {
        /*
         * The batch id, so a row can be quoted in a ticket or matched against a state machine execution —
         * the execution name IS the batch id, so this is the join key between this table and Step Functions.
         *
         * Readable from the sparse index despite the warning above: bulk_promotion_id is the base table's
         * PARTITION KEY, and DynamoDB projects the base table's key attributes into every GSI regardless of
         * the INCLUDE list. Verified against the live index in alpha, whose NonKeyAttributes are only the
         * status, the component type, promoted_by and the five counts.
         */
        id: 'bulkPromotionId',
        header: 'Batch ID',
        sortingField: 'bulkPromotionId',
    },
    STATUS: {
        id: 'statusDisplay',
        header: 'Status',
        sortingField: 'statusDisplay',
    },
    COMPONENT_TYPE: {
        // Keyed on the humanised label so the property filter offers the values the column shows,
        // matching how STATUS is keyed on statusDisplay.
        id: 'componentTypeDisplay',
        header: 'Component Type',
        sortingField: 'componentTypeDisplay',
    },
    REQUESTED: {
        id: 'requestedCount',
        header: 'Requested',
        sortingField: 'requestedCount',
    },
    PROMOTED: {
        id: 'promotedCount',
        header: 'Promoted',
        sortingField: 'promotedCount',
    },
    SKIPPED: {
        id: 'skippedCount',
        header: 'Skipped',
        sortingField: 'skippedCount',
    },
    BLOCKED: {
        id: 'blockedCount',
        header: 'Blocked',
        sortingField: 'blockedCount',
    },
    FAILED: {
        id: 'failedCount',
        header: 'Failed',
        sortingField: 'failedCount',
    },
    PROMOTED_BY: {
        id: 'promotedByDisplay',
        header: 'Promoted By',
        sortingField: 'promotedByDisplay',
    },
    // No On Behalf Of column: the tracker has no such field, the request no longer carries one, and
    // the only caller that set it sent the operator's own alias. Promoted By is the actor of record.
} as const;

export const BULK_PROMOTION_HISTORY_COLUMN_IDS = Object.values(BULK_PROMOTION_HISTORY_COLUMN_DEFINITIONS).map(
    (col) => col.id
);

export const BULK_PROMOTION_HISTORY_DEFAULT_PREFERENCES = {
    pageSize: 20,
    visibleColumns: BULK_PROMOTION_HISTORY_COLUMN_IDS,
    wrapLines: false,
    stripedRows: false,
    contentDisplay: BULK_PROMOTION_HISTORY_COLUMN_IDS.map((id) => ({ id, visible: true })),
    stickyColumns: { first: 0, last: 0 },
};

export const BULK_PROMOTION_HISTORY_PAGE_SIZE_OPTIONS = [
    { value: 10, label: '10 batches' },
    { value: 20, label: '20 batches' },
    { value: 50, label: '50 batches' },
    { value: 100, label: '100 batches' },
];

export const BULK_PROMOTION_HISTORY_VISIBLE_CONTENT_OPTIONS = Object.values(
    BULK_PROMOTION_HISTORY_COLUMN_DEFINITIONS
).map((col) => ({
    id: col.id,
    label: col.header,
    editable: false,
}));

export const BULK_PROMOTION_HISTORY_FILTERING_PROPERTIES = [
    BULK_PROMOTION_HISTORY_COLUMN_DEFINITIONS.STATUS,
    BULK_PROMOTION_HISTORY_COLUMN_DEFINITIONS.COMPONENT_TYPE,
    BULK_PROMOTION_HISTORY_COLUMN_DEFINITIONS.PROMOTED_BY,
    BULK_PROMOTION_HISTORY_COLUMN_DEFINITIONS.STARTED,
].map((column) => ({
    key: column.id,
    operators: [':', '!:', '=', '!='],
    propertyLabel: column.header,
    groupValuesLabel: `${column.header} values`,
}));

/** How many promoted ids the expanded row shows before deferring to Copy all. */
export const BULK_PROMOTION_EXPANDED_ID_LIMIT = 5;
